import { useMemo } from 'react';

import { groupBy } from '@/Pages/Graph/src/utils';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useListTags } from '@/hooks/useSwr';
import { ResourceTag, ResourceTagKeyWithValues } from '@/types/schema';

export function useResourceTagKeysToShow(
  tagsOnResource?: ResourceTag[]
): (ResourceTagKeyWithValues & {
  selected?: ResourceTag[];
  mismatched?: ResourceTag[];
})[] {
  const { selectedTags } = useStoredResourceTags();
  const { data: tagKeysWithValues } = useListTags();

  const selectedTagsGroupedByKey = useMemo(() => {
    return groupBy(selectedTags, (tag) => tag.tag_key_id);
  }, [selectedTags]);

  if (!tagKeysWithValues) {
    return [];
  }

  if (selectedTags.length === 0) {
    const onlyApplication = tagKeysWithValues.filter(
      (tagKey) => tagKey.key === 'Application'
    );

    let noSelectionTags = onlyApplication;
    if (onlyApplication.length === 0) {
      noSelectionTags = tagKeysWithValues;
    }

    const hasCreatedValues = noSelectionTags.some(
      (tagKey) => tagKey.values.length > 0
    );
    if (hasCreatedValues) {
      return noSelectionTags;
    }
    return [];
  }

  const keys = tagKeysWithValues
    .map((tagKey) => {
      return {
        ...tagKey,
        selected: selectedTagsGroupedByKey[tagKey.id],
        mismatched:
          (tagsOnResource &&
            selectedTagsGroupedByKey[tagKey.id]?.filter(
              (tag) =>
                !tagsOnResource.find(
                  (t) =>
                    t.tag_key_id === tag.tag_key_id &&
                    t.tag_value === tag.tag_value
                )
            )) ??
          [],
      };
    })
    .filter((tagKey) => tagKey.selected?.length);

  keys.sort((a, b) => {
    if (a.selected && !b.selected) {
      return -1;
    }
    if (!a.selected && b.selected) {
      return 1;
    }
    if (a.key === 'Application') {
      return -1;
    }
    if (b.key === 'Application') {
      return 1;
    }
    if (a.key === 'Environment') {
      return -1;
    }
    if (b.key === 'Environment') {
      return 1;
    }
    return 0;
  });

  return keys;
}
